'use client';
// Le témoin « l'application travaille » — une goutte qui tombe dans un tube.
//
// ── Le défaut qu'il comble ──────────────────────────────────────────────────
//
// Entre le clic et le message de succès, il ne se passait RIEN à l'écran. Sur
// une action qui met une seconde ou deux — la plupart en écrivent en base puis
// rechargent l'écran — l'utilisateur ne sait pas si son clic a été pris. Il
// reclique. Selon l'action, cela crée un doublon, ou déclenche deux fois un
// envoi. Le retour d'Ines du 18/08 le dit exactement : « se rendre compte que
// le clic a été pris en compte en attendant le pop-up ».
//
// ── Trois corrections du 26/08/2026 ─────────────────────────────────────────
//
//  1. PLUS DE TEXTE. Il disait « Enregistrement en cours… », ce qui devenait
//     FAUX dès qu'il s'est mis à signaler aussi les lectures — et une phrase
//     inexacte coûte plus qu'une absence de phrase. Le tube seul suffit : une
//     forme qui tourne dit « ça travaille » sans avoir à dire quoi.
//
//  2. AU CENTRE DE LA PAGE. Il vivait en bas, là où l'œil n'est pas : on
//     clique au milieu d'un tableau, on regarde le milieu du tableau. Un témoin
//     qu'il faut chercher ne remplit pas son office.
//
//  3. LES LECTURES AUSSI. Il ne connaissait que les écritures ; ouvrir une
//     table ne l'allumait pas, alors que c'est là que l'attente se voit le
//     plus (cf. `lib/attente.ts`).
//
// ── Le délai de 400 ms, qui n'est pas une coquetterie ───────────────────────
//
// Une requête qui répond en 150 ms ferait clignoter le témoin — un éclair que
// l'œil perçoit comme un défaut d'affichage, pas comme une information. En
// n'apparaissant qu'au-delà de 400 ms, il ne se montre que quand l'attente est
// réelle, c'est-à-dire quand il sert. Ce seuil compte double depuis qu'il
// couvre les lectures : la plupart répondent en moins de 200 ms, et sans lui
// l'écran clignoterait à chaque ouverture d'onglet.
import React from 'react';
import { useGed } from '@/lib/store';
import { COUCHES } from '@/components/ui';
import { surAttente, installerTemoinAttente } from '@/lib/attente';

export function TravailEnCours() {
  const ecritures = useGed((e) => e.ecrituresEnCours);
  const [requetes, setRequetes] = React.useState(0);
  /* `pret` n'est PAS « visible » : il dit seulement que les 400 ms sont
     écoulées. La visibilité se DÉDUIT (`actif && pret`) au lieu d'être posée —
     un `setVisible(false)` synchrone dans le corps de l'effet déclenchait un
     rendu en cascade, et le lint le refuse à juste titre : l'information est
     déjà dans `actif`, la stocker deux fois invite à ce qu'elles divergent. */
  const [pret, setPret] = React.useState(false);

  /* L'enrobage est posé au premier montage du témoin — le seul composant qui
     s'en serve. L'installation est idempotente : React monte deux fois en mode
     strict, et sans cela `fetch` s'enroberait lui-même. */
  React.useEffect(() => {
    installerTemoinAttente();
    return surAttente(setRequetes);
  }, []);

  /* Les deux sources sont réunies plutôt que substituées. `ecrituresEnCours`
     couvre `smq`, qui enchaîne l'appel ET le rechargement du magasin : le
     compteur de requêtes retombe à zéro entre les deux, et le témoin
     clignoterait au milieu d'une action qui n'est pas finie. */
  const actif = ecritures > 0 || requetes > 0;

  React.useEffect(() => {
    if (!actif) return undefined;
    const t = window.setTimeout(() => setPret(true), 400);
    // Le nettoyage rend l'état à zéro : la prochaine attente doit repartir de
    // ses 400 ms, sinon deux actions rapprochées feraient apparaître le témoin
    // instantanément à la seconde — le clignotement qu'on cherche à éviter.
    return () => { window.clearTimeout(t); setPret(false); };
  }, [actif]);

  if (!actif || !pret) return null;

  return (
    <div
      // `status` et non `alert` : c'est une information d'avancement, elle ne
      // doit pas interrompre la lecture d'un lecteur d'écran.
      className="ged-travail"
      role="status"
      aria-live="polite"
      // Le texte a disparu de l'écran, pas des lecteurs d'écran : sans lui,
      // une personne aveugle n'a plus AUCUN signal que l'application travaille.
      aria-label="Traitement en cours"
      style={{
        position: 'fixed', insetInlineStart: '50%', top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: COUCHES.TRAVAIL,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        /* Un halo rond plutôt qu'une pastille allongée : sans texte, une pilule
           large autour d'un seul pictogramme se lit comme un bouton vide.
           Diamètre resserré le 26/08 — 62 px faisaient un jeton posé sur la
           page, là où il ne s'agit que d'un signal. */
        width: 46, height: 46, borderRadius: '50%',
        /* ⚠️ PAS de `background` ici, et pas d'`opacity` sur ce bloc : elle
           s'appliquerait AUSSI au tube, qui deviendrait pâle avec le fond. Le
           voile est donc une couche à part, en dessous. */
        boxShadow: 'var(--shadow-sm)',
        /* ⚠️ Le témoin est au MILIEU de la page : sans cela il intercepterait
           les clics au centre de l'écran, c'est-à-dire à l'endroit le plus
           utilisé — et il apparaît précisément quand on vient de cliquer. */
        pointerEvents: 'none',
      }}>
      {/* Le VOILE, en couche séparée : `opacity` posée sur le conteneur aurait
          pâli le tube en même temps que le fond. Ici seul le disque est
          translucide — le tube reste net. */}
      <span aria-hidden style={{
        position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'var(--navy-900)',
        /* 0,78 ne se LISAIT pas comme transparent : sur fond clair, du navy à
           78 % reste un disque plein à l'œil. À 0,5 on voit le contenu au
           travers, ce qui est le but — le témoin signale une attente, il ne
           masque pas la page. */
        opacity: 0.5,
        /* Un flou léger derrière le disque : sans lui, du texte dense passant
           sous un fond à moitié transparent rend le tube illisible. Le flou
           n'assombrit pas, il calme ce qu'il y a dessous. */
        backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
      }} />

      {/* Un tube à essai et sa goutte : le laboratoire plutôt qu'un sablier de
          système d'exploitation. La goutte tombe, le fond se remplit.

          ⚠️ La `viewBox` est SERRÉE sur le dessin (26/08/2026). Elle valait
          « 0 0 16 20 » alors que le tube n'occupe que x 4,3→11,7 et y
          0,8→15,2 : le centre du dessin tombait à 8 pour une boîte centrée sur
          10, et le tube paraissait remonté dans son disque. Centrer le SVG ne
          centrait donc pas ce qu'on voit. */}
      <svg width="11" height="21" viewBox="4.3 0.8 7.4 14.4" aria-hidden="true"
        style={{ position: 'relative', display: 'block' }}>
        <path d="M5 1.5h6M6 1.5v11a2 2 0 0 0 4 0v-11" fill="none"
          stroke="var(--cyan-300)" strokeWidth="1.4" strokeLinecap="round" />
        <path d="M6 11.2c1.4.9 2.6.9 4 0v1.3a2 2 0 0 1-4 0z" fill="var(--cyan-300)" opacity="0.85" />
        <circle cx="8" cy="4" r="1.5" fill="var(--cyan-300)">
          <animate attributeName="cy" values="3.2;9.6;3.2" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.2;0.75;1"
            dur="1.5s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
}
